/**
 * Kalendar uchun sof sana hisoblari (test: dates.test.ts). Sanalar "YYYY-MM-DD" matn sifatida yuradi -- zona
 * chalkashligi yo'q; "bugun" chaqiruvchidan keladi (xodimning vaqt zonasida).
 */
import type { CalendarKind } from "./config";

export interface CalendarEvent {
  id: string;
  date: string;
  /** "HH:MM" yoki null (kunlik voqea) */
  time: string | null;
  kind: CalendarKind;
  title: string;
  subtitle: string;
  driver_id: number | null;
  href: string | null;
  done: boolean;
}

export interface CalendarData {
  from: string;
  to: string;
  today: string;
  events: CalendarEvent[];
  truncated: boolean;
}

export interface GridCell {
  key: string;
  day: number;
  inMonth: boolean;
}

const pad = (n: number) => String(n).padStart(2, "0");

export const keyOf = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(d)}`;

/** UTC asosida kun qo'shish: faqat kalendar sanasi, zona yo'q */
export function addDays(key: string, days: number): string {
  const [y, m, d] = key.split("-").map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + days));
  return keyOf(t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate());
}

export function shiftMonth(year: number, month: number, delta: number): { year: number; month: number } {
  const idx = year * 12 + (month - 1) + delta;
  return { year: Math.floor(idx / 12), month: (idx % 12) + 1 };
}

/** Oy to'ri: dushanbadan boshlanadigan 6 hafta (42 katak), qo'shni oy kunlari `inMonth: false` */
export function monthGrid(year: number, month: number): GridCell[] {
  const first = new Date(Date.UTC(year, month - 1, 1));
  const lead = (first.getUTCDay() + 6) % 7; // 0 -- dushanba
  const cells: GridCell[] = [];
  for (let i = 0; i < 42; i++) {
    const t = new Date(Date.UTC(year, month - 1, 1 - lead + i));
    cells.push({ key: keyOf(t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate()), day: t.getUTCDate(), inMonth: t.getUTCMonth() === month - 1 });
  }
  return cells;
}

/** To'r qamrab olgan oraliq (qo'shni oy kunlari ham nuqta bilan ko'rinsin) */
export function gridRange(year: number, month: number): { from: string; to: string } {
  const cells = monthGrid(year, month);
  return { from: cells[0].key, to: cells[cells.length - 1].key };
}

export function groupByDate(events: CalendarEvent[]): Map<string, CalendarEvent[]> {
  const map = new Map<string, CalendarEvent[]>();
  for (const e of events) {
    const list = map.get(e.date);
    if (list) list.push(e);
    else map.set(e.date, [e]);
  }
  return map;
}

/** Bugundan boshlab yaqin voqealar (bajarilganlarsiz), eng ko'pi `limit` ta */
export function upcoming(events: CalendarEvent[], today: string, limit: number): CalendarEvent[] {
  return events.filter((e) => e.date >= today && !e.done).slice(0, limit);
}

/** Har kunda ko'rinadigan nuqtalar: tur bo'yicha takrorsiz, eng ko'pi 3 ta */
export function dotKinds(events: CalendarEvent[] | undefined, max = 3): CalendarKind[] {
  const out: CalendarKind[] = [];
  for (const e of events ?? []) {
    if (e.done || out.includes(e.kind)) continue;
    out.push(e.kind);
    if (out.length === max) break;
  }
  return out;
}

/** "Bugun" / "Ertaga" / sana -- ro'yxat sarlavhasi uchun */
export function relativeDay(key: string, today: string): "today" | "tomorrow" | "other" {
  if (key === today) return "today";
  if (key === addDays(today, 1)) return "tomorrow";
  return "other";
}
